import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Heart, Check, Loader2, Building2, AlertCircle } from 'lucide-react';

const MAX = 600;

const CharityThankYou = () => {
  const { token } = useParams();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ author_name: '', author_title: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/api/charity-notes/invite/${token}`)
      .then(r => setContext(r.data))
      .catch(e => setError(e.response?.data?.detail || 'This link is no longer active'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.post(`/api/charity-notes/invite/${token}`, form);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send your note. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!context) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center bg-slate-50" data-testid="thank-you-invalid">
        <AlertCircle className="w-10 h-10 text-amber-500 mb-4" />
        <h1 className="text-xl font-bold text-slate-900">Link not available</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-sm">{error}</p>
        <Link to="/" className="mt-6 text-sm font-semibold text-blue-600 hover:text-blue-700">Go to Altrunic →</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-14 px-6" data-testid="charity-thank-you-page">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2.5 mb-8">
          <img src="/logo.png" alt="Altrunic" className="w-8 h-8 object-contain" />
          <span className="text-lg font-bold tracking-tight text-slate-900">Altrunic</span>
        </div>

        {sent ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center shadow-sm" data-testid="thank-you-success">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center mx-auto mb-5">
              <Check className="w-7 h-7 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Thank you — note received</h2>
            <p className="text-sm text-slate-500 mt-3 leading-relaxed">
              {context.company_name} will review your message shortly. Once approved, every donor who supported
              {' '}{context.campaign_title || 'this campaign'} will see it on their donation receipt.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-8 pt-8 pb-6 border-b border-slate-100">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-4">
                <Heart className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-[11px] font-semibold text-blue-700 tracking-[0.12em] uppercase">Thank-you note</span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="thank-you-charity-name">
                A message from {context.charity_name}
              </h1>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed">
                Employees at <strong className="text-slate-800">{context.company_name}</strong> raised{' '}
                <strong className="text-slate-800">${(context.raised_amount || 0).toLocaleString()}</strong> for you
                {context.campaign_title ? <> through <strong className="text-slate-800">{context.campaign_title}</strong></> : null}.
                Write them a short note — donors see it on their receipt.
              </p>
            </div>

            {context.already_submitted && (
              <p className="mx-8 mt-6 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="thank-you-already-submitted">
                A note has already been submitted through this link. Anything you send now will be added as an additional message.
              </p>
            )}

            <form onSubmit={submit} className="p-8 space-y-4" data-testid="thank-you-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Your name *</label>
                  <input required value={form.author_name} onChange={e => setForm({ ...form, author_name: e.target.value })}
                    placeholder="Dana Alvarez"
                    className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-blue-500"
                    data-testid="thank-you-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Role <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input value={form.author_title} onChange={e => setForm({ ...form, author_title: e.target.value })}
                    placeholder="Director of Programs"
                    className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-blue-500"
                    data-testid="thank-you-title-input" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Your note *</label>
                <textarea required rows={5} maxLength={MAX} value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  placeholder="Because of your team, we were able to…"
                  className="w-full border border-slate-300 rounded-xl px-3.5 py-3 text-sm focus:outline-none focus:border-blue-500 resize-none"
                  data-testid="thank-you-message-input" />
                <p className="mt-1.5 text-xs text-slate-400 text-right" data-testid="thank-you-char-count">
                  {form.message.length}/{MAX}
                </p>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="thank-you-error">{error}</p>
              )}

              <button type="submit" disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-xl text-sm transition-colors"
                data-testid="thank-you-submit-btn">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Send my thank-you'}
              </button>
              <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> {context.company_name} reviews notes before donors see them
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default CharityThankYou;
